/**
 * 上传成功的文件信息
 * @description 字段名使用 snake_case 以匹配后端返回格式
 */
export interface UploadedFileInfo {
    /** 原始文件名 */
    original_file_name: string;
    /** 存储的文件名 */
    file_name: string;
    /** 存储的文件名（兼容旧字段） */
    stored_name?: string;
    /** 文件访问URL */
    file_url: string;
    /** 文件大小（字节） */
    file_size: number;
    /** 文件类型 */
    content_type: string;
    /** S3存储键 */
    s3_key: string;
    /** 文件扩展名 */
    file_extension?: string;
    /** 文件MD5 */
    md5?: string;
    /** 文件标签 */
    tags?: string[];
    /** 上传时间 */
    upload_time?: string;
    /** 是否为新上传 */
    is_new_upload?: boolean;
}

/**
 * 上传失败的文件信息
 * @description 字段名使用 snake_case 以匹配后端返回格式
 */
export interface FailedFileInfo {
    /** 原始文件名 */
    file_name: string;
    /** 失败原因 */
    error_message: string;
    /** 失败原因错误码 */
    error_code?: number;
    /** 文件大小（字节） */
    file_size: number;
}

/**
 * 文件上传响应（单文件/批量通用）
 */
export interface GeneralFileUploadResponse {
    /** 上传成功的文件列表 */
    uploaded_files: UploadedFileInfo[];
    /** 上传失败的文件列表 */
    failed_files: FailedFileInfo[];
    /** 总文件数 */
    total_count: number;
    /** 成功上传数 */
    success_count: number;
    /** 失败上传数 */
    failed_count: number;
}

/**
 * LOC分类项
 */
export interface LocItem {
    /** LOC分类号 */
    id: string;
    /** LOC解释 */
    desc?: string;
}

type DateOperator = 'gte' | 'lte' | 'gt' | 'lt' | 'eq'

/**
 * 外观FTO搜索条件
 */
export interface DesignFtoSearchConditions {
    /** 任务ID（必填，由前端生成，用于外部服务追踪） */
    task_id: string;
    /** 目标图片path */
    target_image_key: string;
    /** 目标图片URL */
    target_image_url: string;
    /** 受理局代码列表（可选） */
    country?: string[];
    /** LOC分类项列表（可选） */
    loc_items?: LocItem[];
    /** 主要字段（关键词，可选） */
    main_field?: string;
    /** 日期过滤条件（可选） */
    date_filters?: {
        APD?: {
            operator: DateOperator;
            value: string;
        };
        PBD?: {
            operator: DateOperator;
            value: string;
        };
    };
    /** 历史记录标题 */
    title: string;
}

export interface DesignFtoBatchTaskResponse {
    /** 批次ID */
    batch_id: string;
    /** 任务总数 */
    total_count: number;
    /** 预估时间 */
    estimated_duration: number;
}

/**
 * 外观FTO API服务接口
 */
export interface DesignFtoApi {
    /**
     * 上传文件
     */
    uploadFile(file: File, onProgress?: (e: any) => void): Promise<UploadedFileInfo>;

    /**
     * 提交批量任务
     * @param images - 图片列表（1-100个）
     */
    submitBatchTask(images: DesignFtoSearchConditions[]): Promise<DesignFtoBatchTaskResponse>;
}

/**
 * DesignFtoApi 的 IOC 注入标识符
 */
export const DesignFtoApi = Symbol('DesignFtoApi');
